import { environment } from '@environments/environment';
import { ReportingOpportunityService } from '@http/clients/reporting-app/reporting-opportunity.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatNumbersCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    bidCategoriesEditorConfig,
    companyEditorConfig,
    competingBidderEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    expectedResponseDateIncludeEmptyEditorConfig,
    includeArchivedEditorConfig,
    marketsEditorConfig,
    opportunityCompanyRoleEditorConfig,
    opportunityCreationDateIncludeEmptyEditorConfig,
    opportunityDueDateIncludeEmptyEditorConfig,
    opportunitySourceEditorConfig,
    opportunityStatusEditorConfig,
    potentialValueEditorConfig,
    probabilityEditorConfig,
    productTypesEditorConfig,
    projectEndDateIncludeEmptyEditorConfig,
    projectStartDateIncludeEmptyEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const Opportunities: IReportConfiguration<ReportingOpportunityService> = {
    base: {
        type: ReportType.Opportunities,
        serviceType: ReportingOpportunityService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getOpportunityBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getOpportunityMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'PotentialValue', label: 'Potential Value ($)', format: 'currency' },
                        { name: 'ProposedGrossMarginPercentage', label: 'Potential GM (%)', format: 'customPercent' },
                        { name: 'ActualValue', label: 'Actual Value ($)', format: 'currency' },
                        { name: 'ActualGrossMarginPercentage', label: 'Actual GM (%)', format: 'customPercent' }
                    ]
                }
            },
            {
                methodName: 'getOpportunityLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Opportunities by Actual Value'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'opportunityName',
                            yKey: 'potentialValue',
                            yName: 'Potential Value ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'opportunityName',
                            yKey: 'actualValue',
                            yName: 'Actual Value ($)'
                        },
                        {
                            type: 'line',
                            xKey: 'opportunityName',
                            yKey: 'proposedGrossMarginPercentage',
                            yName: 'Potential GM (%)'
                        },
                        {
                            type: 'line',
                            xKey: 'opportunityName',
                            yKey: 'actualGrossMarginPercentage',
                            yName: 'Actual GM (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['potentialValue', 'actualValue'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['proposedGrossMarginPercentage', 'actualGrossMarginPercentage'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getOpportunityColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'bidAmount',
                            yName: 'Bid Amount'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'awardedAmount',
                            yName: 'Awarded Amount'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'opportunityValue',
                            yName: 'Opportunity Value'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'acceptedValuePercentage',
                            yName: 'Accepted Value (%)'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'acceptedProjectsPercentage',
                            yName: 'Accepted Projects (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['bidAmount', 'awardedAmount', 'opportunityValue'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['acceptedValuePercentage', 'acceptedProjectsPercentage'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getOpportunityValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'entityId',
                        headerName: 'OID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return `<div></div>`;
                        }
                    },
                    { field: 'opportunityName', headerName: 'Opportunity Name', enableRowGroup: true },
                    { field: 'dueDate', headerName: 'Due Date', type: 'date' },
                    { field: 'creationDate', headerName: 'Creation Date', type: 'date' },
                    { field: 'expectedBookingDate', headerName: 'Expected Booking Date', type: 'date' },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'companyNames', headerName: 'Companies', enableRowGroup: true },
                    { field: 'companyRoleNames', headerName: 'Company Roles', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'actualValue', headerName: 'Actual Value ($)', type: 'currency', aggFunc: 'sum' },
                    { field: 'avgTotalTerm', headerName: 'Term Average', type: 'currency', aggFunc: 'avg' },
                    {
                        field: 'totalTerm',
                        headerName: '# of Terms',
                        aggFunc: 'avg',
                        valueFormatter: (params: any) => {
                            if (!params.value) return null;
                            const value = Number(params.value);
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        }
                    },
                    {
                        field: 'actualGRossMarginPercentage',
                        headerName: 'Actual GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'bid',
                        headerName: 'BID',
                        enableRowGroup: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (!params.value) {
                                return;
                            }
                            if (params.data?.projectArchived) {
                                return params.value;
                            } else {
                                const link = `${environment.SalesAppClientBaseUrl}/${environment.SalesAppClientProjectOverviewUrl}/${params.value}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                        }
                    },
                    { field: 'projectID', headerName: 'Project ID' },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'status', headerName: 'Status', enableRowGroup: true },
                    { field: 'probability', headerName: 'Probability', type: 'percentageString', aggFunc: 'avg' },
                    { field: 'buildingName', headerName: 'Main Building Name', enableRowGroup: true },
                    { field: 'potentialValue', headerName: 'Potential Value ($)', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'proposedGrossMarginPercent',
                        headerName: 'Potential GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'potentialTermAverage',
                        headerName: 'Potential Term Average',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'potentialNumberOfTerms',
                        headerName: 'Potential # of Terms',
                        aggFunc: 'avg',
                        valueFormatter: (params: any) => {
                            if (!params.value) return null;
                            const value = Number(params.value);
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        }
                    },
                    { field: 'speculativeValue', headerName: 'Speculative Value', type: 'currency', aggFunc: 'sum' },
                    { field: 'totalBuildingSqFt', headerName: 'Total Building Sq. Ft.' },
                    { field: 'sellPriceSqFt', headerName: 'Sell Price / Sq. Ft.', aggFunc: 'sum' },
                    { field: 'market', headerName: 'Market' },
                    { field: 'productTypes', headerName: 'Product Types', enableRowGroup: true },
                    { field: 'leadSource', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'projectDuration', headerName: 'Project Duration (days)', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'competingBidder', headerName: 'Competing Bidder', enableRowGroup: true },
                    { field: 'bidCategories', headerName: 'Bid Categories', enableRowGroup: true },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            opportunityDueDateIncludeEmptyEditorConfig,
            opportunityCreationDateIncludeEmptyEditorConfig,
            projectStartDateIncludeEmptyEditorConfig,
            projectEndDateIncludeEmptyEditorConfig,
            marketsEditorConfig,
            productTypesEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            opportunityStatusEditorConfig,
            customerEditorConfig,
            opportunityCompanyRoleEditorConfig,
            companyEditorConfig,
            accountEditorConfig,
            competingBidderEditorConfig,
            bidCategoriesEditorConfig,
            potentialValueEditorConfig,
            probabilityEditorConfig,
            expectedResponseDateIncludeEmptyEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
